import _sumBy from 'lodash/sumBy';
import _round from 'lodash/round';

interface ScaledColumnWidth {
    id: string;
    width: number;
}

/**
 * Scale an array of ColumnConfigInputs such that those which can scale are scaled up to fill the
 * availableWidth, and those that cannot scale remain as they are.
 * @param {number} availableWidth total width available to fill with columns
 * @param {ColumnConfigInput[]} columnConfigs
 * @returns {ColumnConfigOutput[]}
 */
export const getScaledColumnWidths = (
    availableWidth: number,
    columnWidthInputs: Record<string, ColumnWidthInput>,
): Record<string, number> => {
    const widthMaps: ColumnWidthMap[] = [];
    let totalMin = 0;
    let containsNoScalableWidths = true;

    Object.entries(columnWidthInputs).forEach(([id, { min, max }]): void => {
        totalMin += min;

        if (min !== max) {
            containsNoScalableWidths = false;
        }

        widthMaps.push({
            id,
            min,
            max,
        });
    });

    if (totalMin >= availableWidth) {
        if (localStorage.environment !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(
                `To avoid negative scaling on column widths, totalMin should be less than or equal to availableWidth.
                totalMin = ${totalMin}
                availableWidth = ${availableWidth}
                Check your column width definitions.
                `,
            );
        }

        return Object.entries(columnWidthInputs).reduce(
            (
                acc: Record<string, number>,
                [id, { min }],
            ): Record<string, number> => {
                acc[id] = min;

                return acc;
            },
            {} as Record<string, number>,
        );
    }

    const calculated: ScaledColumnWidth[] = [];
    const widthToUse = availableWidth - totalMin;

    let widthUsed = 0;
    const widthMapsLeftToScale: ColumnWidthMap[] = [];

    widthMaps.forEach((widthMap: ColumnWidthMap): void => {
        const { id, min, max } = widthMap;
        const ratio = min / totalMin;
        const widthByRatio = min + ratio * widthToUse;

        if (max === min) {
            widthUsed += min;
            calculated.push({ id, width: min });
        } else if (max === undefined) {
            widthMapsLeftToScale.push(widthMap);
        } else if (widthByRatio < max) {
            widthUsed += widthByRatio;
            calculated.push({ id, width: widthByRatio });
        } else {
            widthUsed += max;
            calculated.push({ id, width: max });
        }
    });

    const widthLeftToUse = availableWidth - widthUsed;
    const totalMinOfWidthMapsLeftToScale = _sumBy(widthMapsLeftToScale, 'min');

    widthMapsLeftToScale.forEach(({ id, min }: ColumnWidthMap): void => {
        const ratio = min / totalMinOfWidthMapsLeftToScale;
        const widthByRatio = ratio * widthLeftToUse;

        calculated.push({ id, width: widthByRatio });
    });

    const rounded = calculated.map(
        ({ id, width }: ScaledColumnWidth): ScaledColumnWidth => ({
            id,
            width: _round(width),
        }),
    );

    const roundedSum = _sumBy(rounded, 'width');

    if (!containsNoScalableWidths) {
        const difference = availableWidth - roundedSum;

        if (rounded[0] !== undefined) {
            const firstWidth = rounded[0].width;

            rounded[0].width = firstWidth + difference;
        }
    }

    return rounded.reduce(
        (
            acc: Record<string, number>,
            { id, width }: ScaledColumnWidth,
        ): Record<string, number> => {
            acc[id] = width;

            return acc;
        },
        {} as Record<string, number>,
    );
};
